// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Light/dark color mode. Cloudscape switches its design tokens on the `awsui-dark-mode` class that applyMode puts
// on <body>; the TxDOT overrides in txdotTheme.ts carry a value for each mode. The user's choice is kept in
// localStorage (a display preference, not a credential); with no choice saved, the mode follows the operating
// system's prefers-color-scheme setting, including when it changes while the app is open.
import { useSyncExternalStore } from 'react';
import { applyMode, Mode } from '@cloudscape-design/global-styles';

export type ColorMode = 'light' | 'dark';

export const COLOR_MODE_KEY = 'bda:colorMode';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export function storedColorMode(): ColorMode | null {
  try {
    const v = window.localStorage.getItem(COLOR_MODE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function darkQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
}

export function systemColorMode(): ColorMode {
  return darkQuery()?.matches ? 'dark' : 'light';
}

/** The saved choice, else the operating system's preference. */
export function preferredColorMode(): ColorMode {
  return storedColorMode() ?? systemColorMode();
}

let current: ColorMode = 'light';
const listeners = new Set<() => void>();

function apply(mode: ColorMode): void {
  current = mode;
  applyMode(mode === 'dark' ? Mode.Dark : Mode.Light);
  // Native controls, scrollbars, and the page canvas outside Cloudscape (e.g. the startup error) follow too.
  document.documentElement.style.colorScheme = mode;
  listeners.forEach((l) => l());
}

let systemWatch: { query: MediaQueryList; onChange: () => void } | null = null;

/** Apply the preferred mode and keep following the OS setting while no choice is saved. Call once at startup. */
export function initColorMode(): ColorMode {
  apply(preferredColorMode());
  const query = darkQuery();
  if (query && !systemWatch) {
    const onChange = () => {
      if (!storedColorMode()) apply(systemColorMode());
    };
    query.addEventListener('change', onChange);
    systemWatch = { query, onChange };
  }
  return current;
}

/** Switch mode and remember the choice. */
export function setColorMode(mode: ColorMode): void {
  try {
    window.localStorage.setItem(COLOR_MODE_KEY, mode);
  } catch {
    // Storage blocked (private mode, policy): the switch still applies for this page.
  }
  apply(mode);
}

export function colorMode(): ColorMode {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current mode; re-renders on change. */
export function useColorMode(): ColorMode {
  return useSyncExternalStore(subscribe, colorMode, colorMode);
}

/** Tests: forget the OS listener and return to light mode. */
export function resetColorModeForTests(): void {
  if (systemWatch) systemWatch.query.removeEventListener('change', systemWatch.onChange);
  systemWatch = null;
  apply('light');
  document.documentElement.style.colorScheme = '';
}
